/**
 * 25 Oct 2026 (D-1025-TODAY, whole-app review §3.3) — Needs your attention on Today: one list grouped by program, in the
 * program order (Housekeeping · Companion · Reading & Services · Tech Support · Events · Volunteers). Each group's heading
 * opens its program; each line NAMES the person or thing and opens the exact place it is fixed; three lines by deadline,
 * then "and N more" (the program). It ends with "Nothing else needs your attention." so a screen-reader user knows they
 * heard it all. Today changes nothing: every line is a link.
 */
import { useMemo, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HOUSE_PROGRAMS, PROGRAM_HOME, groupEntries, type HouseAttnEntry, type HouseAttnLine } from '@shared/house-today';
import { useOpenIn, type Program } from '@/app/program';
import { entryWords } from './entryWords';
import { programWord } from './words';
import '@/styles/today1025.css';

function OpenLink({ href, program, children, ...rest }: { href: string; program: Program | null; children: ReactNode; className?: string; 'data-testid'?: string; 'aria-label'?: string }) {
  const openIn = useOpenIn();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openIn(href, program);
  };
  return (
    <Link to={href} onClick={onClick} {...rest}>
      {children}
    </Link>
  );
}

/** The address of an entry: its own, else — a count alone — its line's. */
const hrefOf = (e: HouseAttnEntry) => (e.item ? e.item.href : e.line.href);
const openInOf = (e: HouseAttnEntry): Program | null => (e.item ? e.item.openIn : e.line.openIn);

/** A group made only of the inbox and failed-email lines is Messages, not a program. */
const onlyInbox = (lines: readonly HouseAttnLine[]) => lines.length > 0 && lines.every((l) => l.kind === 'inbox' || l.kind === 'emailFailed');

export function AttentionGroups({ lines }: { lines: readonly HouseAttnLine[] }) {
  const { t, i18n } = useTranslation();
  const groups = useMemo(() => HOUSE_PROGRAMS.map((p) => ({ program: p, ...groupEntries(lines, p) })).filter((g) => g.entries.length > 0), [lines]);

  return (
    <section aria-labelledby="h-t1025-attn" className="t184-section t1025-attn" data-testid="t184-attn">
      <h2 id="h-t1025-attn">{t('today184.attnTitle')}</h2>
      {groups.map((g) => {
        const mine = lines.filter((l) => l.program === g.program);
        const inbox = onlyInbox(mine);
        const base = PROGRAM_HOME[g.program];
        const home: { href: string | null; openIn: Program | null } = inbox ? { href: mine[0]!.href, openIn: null } : { href: mine.some((l) => l.href) ? base.href : null, openIn: base.openIn };
        const name = inbox ? t('today1025.messages') : programWord(g.program, t);
        const heading = `${name} · ${g.count}`;
        return (
          <div key={g.program} className="t1025-group" data-cat={g.program} data-testid={`t1025-group-${g.program}`}>
            <h3 id={`h-t1025-${g.program}`} className="t1025-gh">
              {home.href ? (
                <OpenLink href={home.href} program={home.openIn} aria-label={t('today1025.groupOpen', { program: name }) + ` — ${heading}`} data-testid={`t1025-group-open-${g.program}`}>
                  {heading}
                </OpenLink>
              ) : (
                heading
              )}
            </h3>
            <ul className="t1025-lines" aria-labelledby={`h-t1025-${g.program}`}>
              {g.shown.map((e) => {
                const words = entryWords(e, t, i18n.language);
                const href = hrefOf(e);
                return (
                  <li key={e.key} data-testid={`t1025-item-${e.key}`} data-line={e.line.id}>
                    {href ? (
                      <OpenLink href={href} program={openInOf(e)} className="t1025-line" data-testid={`t1025-open-${e.key}`}>
                        <span className="t1025-what">{words}</span>
                        <span className="t1025-go" aria-hidden="true">
                          ›
                        </span>
                      </OpenLink>
                    ) : (
                      <span className="t1025-line t1025-plain">
                        <span className="t1025-what">{words}</span>
                      </span>
                    )}
                  </li>
                );
              })}
              {g.more > 0 ? (
                <li className="t1025-more">
                  {home.href ? (
                    <OpenLink href={home.href} program={home.openIn} className="t1025-line" aria-label={t('today1025.moreAria', { count: g.more, program: name })} data-testid={`t1025-more-${g.program}`}>
                      <span className="t1025-what">{t('today1025.more', { count: g.more })}</span>
                      <span className="t1025-go" aria-hidden="true">
                        ›
                      </span>
                    </OpenLink>
                  ) : (
                    <span className="t1025-line t1025-plain">{t('today1025.more', { count: g.more })}</span>
                  )}
                </li>
              ) : null}
            </ul>
          </div>
        );
      })}
      <p className="help t1025-end" data-testid="t1025-attn-end">
        {t('today1025.attnNone')}
      </p>
    </section>
  );
}
